import { useEffect, useRef } from "react"

export interface ParticlesBackgroundRef {
  triggerLoginAnimation: () => void
  resetParticles: () => void
}

declare global {
  interface Window {
    particlesJS: (id: string, config: object) => void
    pJSDom: Array<{ pJS: ParticlesInstance }>
  }
}

interface ParticlesInstance {
  particles: {
    move: { speed: number }
    size: { value: number }
    opacity: { value: number }
    color: { value: string }
    line_linked: { color: string }
  }
  fn: {
    modes: {
      pushParticles: (count: number, pos?: { x: number; y: number }) => void
    }
  }
}

export function ParticlesBackground({ onReady }: { onReady?: (ref: ParticlesBackgroundRef) => void }) {
  const initialized = useRef(false)
  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    // Load particles.js from CDN
    const script = document.createElement("script")
    script.src = "https://cdn.jsdelivr.net/npm/particles.js@2.0.0/particles.min.js"
    script.async = true
    script.onload = () => {
      window.particlesJS("particles-js", {
        particles: {
          number: { value: 140, density: { enable: true, value_area: 800 } },
          size: { value: 2 },
          color: { value: "#ffffff" },
          opacity: { value: 0.5 },
          line_linked: {
            enable: true,
            distance: 120,
            color: "#ffffff",
            opacity: 0.15,
            width: 1
          },
          move: { enable: true, speed: 1.4 }
        },
        interactivity: {
          detect_on: "canvas",
          events: {
            onhover: { enable: true, mode: "repulse" },
            onclick: { enable: false }
          },
          modes: {
            repulse: { distance: 80 }
          }
        }
      })

      // Expose animation functions
      let animationTimeout: ReturnType<typeof setTimeout> | null = null

      const resetToNormal = () => {
        // Cancelar timeout pendiente
        if (animationTimeout) {
          clearTimeout(animationTimeout)
          animationTimeout = null
        }

        if (window.pJSDom && window.pJSDom[0]) {
          const pJS = window.pJSDom[0].pJS
          // Cambiar configuracion
          pJS.particles.move.speed = 1.4
          pJS.particles.size.value = 2
          pJS.particles.opacity.value = 0.5
          pJS.particles.color.value = "#ffffff"
          pJS.particles.line_linked.color = "#ffffff"

          // Forzar actualizacion de particulas existentes
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const particlesArray = (pJS as any).particles.array
          if (particlesArray) {
            particlesArray.forEach((p: { color: { value: string }; }) => {
              p.color.value = "#ffffff"
            })
          }
        }
      }

      onReadyRef.current?.({
        triggerLoginAnimation: () => {
          if (window.pJSDom && window.pJSDom[0]) {
            const pJS = window.pJSDom[0].pJS

            // Cambiar configuracion a verde y más rápido
            pJS.particles.move.speed = 8
            pJS.particles.size.value = 6
            pJS.particles.opacity.value = 1
            pJS.particles.color.value = "#00ff88"
            pJS.particles.line_linked.color = "#00ff88"

            // Forzar actualizacion de particulas existentes a verde
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const particlesArray = (pJS as any).particles.array
            if (particlesArray) {
              particlesArray.forEach((p: { color: { value: string }; }) => {
                p.color.value = "#00ff88"
              })
            }

            // Agregar más partículas
            for (let i = 0; i < 80; i++) {
              pJS.fn.modes.pushParticles(1, {
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight
              })
            }

            // Volver a la normalidad después de 2 segundos
            animationTimeout = setTimeout(resetToNormal, 2000)
          }
        },
        resetParticles: resetToNormal
      })
    }
    document.body.appendChild(script)

    return () => {
      // Cleanup on unmount
      const existingScript = document.querySelector('script[src*="particles.js"]')
      if (existingScript) {
        existingScript.remove()
      }
    }
  }, [])

  return (
    <div
      id="particles-js"
      style={{
        position: "fixed",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        zIndex: 0
      }}
    />
  )
}
